import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, MetinAlani, Rozet, Tablo } from "@koclukpanel/ui";
import {
  ApiHatasi,
  genelOnayBekleyenler,
  genelOnayKarar,
  type OnayDurumu,
  type OnayTalebi,
} from "./api";

/**
 * GENEL ONAY KUYRUGU — F-APR-01/05
 *
 * ================================================================
 * VELI RAPORU ONAYIYLA KARISTIRILMASIN
 * ================================================================
 * Veli raporu onayi AYRI bir yoldan gidiyor (`OnayKuyrugu`, sekmenin
 * digeri). Bu ekran JENERIK motoru gosteriyor: `entityType` +
 * `entityId` ile herhangi bir kayda acilmis onay talebi.
 *
 * Ikisi ayni sekme grubunda cunku onaycinin sordugu soru tek: "kim
 * benden ne bekliyor". Ayri menu girdisi olsalardi, kuyrugu bosaltan
 * kisi ikinci bir yeri kontrol etmeyi unuturdu.
 *
 * ================================================================
 * GECISLERI SUNUCU SOYLUYOR
 * ================================================================
 * Ekranin kendi gecis tablosu YOK. Hangi karara gidilebilecegi her
 * talepte `izinliGecisler` ile geliyor; kural sunucuda degisince
 * arayuz kendiliginde dogru kaliyor. Kendi tablomuzu tutsaydik,
 * kullanicinin gordugu dugme ile sunucunun kabul ettigi karar
 * birbirinden ayrildigi gun ekran "basiyorum, hata veriyor" haline
 * gelirdi.
 */

const DURUM_ADI: Record<OnayDurumu, string> = {
  DRAFT: "Taslak",
  PENDING: "Bekliyor",
  IN_REVIEW: "İnceleniyor",
  REVISION_REQUESTED: "Revizyon istendi",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  CANCELLED: "İptal",
};

const DURUM_TONU: Record<OnayDurumu, "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  DRAFT: "notr",
  PENDING: "uyari",
  IN_REVIEW: "bilgi",
  REVISION_REQUESTED: "uyari",
  APPROVED: "onay",
  REJECTED: "hata",
  CANCELLED: "notr",
};

/**
 * `entityType` ham model adi — "WeeklySchedule" yazan bir kuyruk
 * kullanilamaz. Taninmayan turde ham ada DUSUYOR: "Diğer" yazmak,
 * yeni bir tur eklendigi gun butun satirlari ayni gorunmez yapardi.
 */
const TUR_ADI: Record<string, string> = {
  WeeklySchedule: "Haftalık program",
  ParentReport: "Veli raporu",
  CurriculumVersion: "Müfredat sürümü",
  Exam: "Deneme tanımı",
  Document: "Doküman",
  LeaveRequest: "İzin talebi",
};

/**
 * Kararın GÖRÜNÜMÜ — çizimin iki tonu.
 *
 * `Kuyruk.dc.html`: kabul birincil, ret **halkalı** (dolu kırmızı
 * değil). Ötekiler ikincil: revizyon istemek ne kutlanacak ne
 * cezalandırılacak bir karar, işin normal parçası.
 *
 * Tablo `Record` DEĞİL, kısmi: sunucu yarın yeni bir geçiş eklerse
 * düğme `ikincil` olarak çizilir ve ekran çalışmaya devam eder.
 * Tam `Record` olsaydı derleme kırılırdı — ama burada kırılmanın
 * kazandıracağı bir şey yok, çünkü doğru varsayılan belli.
 */
const KARAR_GORUNUMU: Partial<Record<OnayDurumu, "birincil" | "tehlike" | "ikincil">> = {
  APPROVED: "birincil",
  REJECTED: "tehlike",
};

/** Gerekce ZORUNLU olan kararlar — sunucudaki `GEREKCE_ISTEYEN` ile ayni. */
const GEREKCE_ISTEYEN: readonly OnayDurumu[] = ["REJECTED", "REVISION_REQUESTED"];

const ONCELIK_ADI: Record<number, string> = {
  1: "Acil",
  2: "Normal",
  3: "Düşük",
  4: "En düşük",
};

function bekleme(damga: string): string {
  const dakika = Math.floor((Date.now() - new Date(damga).getTime()) / 60000);
  if (dakika < 60) return "az önce";
  const saat = Math.floor(dakika / 60);
  if (saat < 24) return `${saat} saattir`;
  return `${Math.floor(saat / 24)} gündür`;
}

export function GenelOnaylar() {
  const [acik, setAcik] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ["genel-onaylar"],
    queryFn: genelOnayBekleyenler,
    retry: false,
  });

  if (liste.isPending) return <Iskelet satir={3} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Onay talepleri alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (liste.data.length === 0) {
    return (
      <BosDurum
        ikon="tamam"
        baslik="Kararınızı bekleyen talep yok"
        /*
          "Talep yok" demiyor: bu kuyruk KENDI taleplerini
          gostermiyor ve kapsam disindakileri de gostermiyor. "Hiç
          talep yok" demek, aslinda var olan bir seyi yok saydirirdi.
        */
        aciklama="Program, müfredat ve doküman onayları burada birikir. Kendi açtığınız talepler bu listede görünmez."
        eylem={
          <Buton gorunum="sessiz" onClick={() => void liste.refetch()}>
            Yenile
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-notr-600 text-sm">{liste.data.length} talep kararınızı bekliyor</p>
        <Buton gorunum="sessiz" onClick={() => void liste.refetch()} yukleniyor={liste.isFetching}>
          Yenile
        </Buton>
      </div>

      <Tablo
        satirlar={liste.data}
        anahtar={(t) => t.id}
        kartBasligi={0}
        sutunlar={[
          {
            baslik: "Konu",
            mobil: true,
            className: "font-medium",
            /*
              `summary` varsa O gosteriliyor: talebi acan kisi neyi
              onaylatmak istedigini kendi cumlesiyle yazmis. Yoksa
              tur adina dusuyor — ham kimlik ASLA tek basina
              gosterilmiyor.
            */
            hucre: (t) => t.summary ?? TUR_ADI[t.entityType] ?? t.entityType,
          },
          {
            baslik: "Tür",
            mobil: true,
            hucre: (t) => TUR_ADI[t.entityType] ?? t.entityType,
          },
          { baslik: "İsteyen", mobil: true, hucre: (t) => t.talepSahibi ?? "—" },
          { baslik: "Öğrenci", hucre: (t) => t.ogrenciAdi ?? "—" },
          {
            baslik: "Öncelik",
            hucre: (t) =>
              t.priority === 1 ? (
                <Rozet ton="uyari">Acil</Rozet>
              ) : (
                <span className="text-notr-600">{ONCELIK_ADI[t.priority] ?? t.priority}</span>
              ),
          },
          {
            baslik: "Bekleme",
            mobil: true,
            /* "Ne zaman açıldı" değil "ne kadardır bekletiyorum". */
            hucre: (t) => bekleme(t.createdAt),
          },
          {
            baslik: "Durum",
            hucre: (t) => <Rozet ton={DURUM_TONU[t.status]}>{DURUM_ADI[t.status]}</Rozet>,
          },
          {
            baslik: "İşlem",
            mobil: true,
            hucre: (t) => (
              <div className="flex justify-end">
                <Buton gorunum="sessiz" onClick={() => setAcik(acik === t.id ? null : t.id)}>
                  {acik === t.id ? "Kapat" : "Karara bağla"}
                  <span className="sr-only">
                    {" "}
                    — {t.summary ?? TUR_ADI[t.entityType] ?? t.entityType}
                  </span>
                </Buton>
              </div>
            ),
          },
        ]}
        altSatir={(t) =>
          acik === t.id ? (
            <div className="border-notr-100 bg-notr-50 border-b px-3 py-3">
              <KararFormu talep={t} onBitti={() => setAcik(null)} />
            </div>
          ) : null
        }
      />
    </div>
  );
}

function KararFormu({ talep, onBitti }: { talep: OnayTalebi; onBitti: () => void }) {
  const qc = useQueryClient();

  /*
    Secenekler `izinliGecisler`den suzuluyor ve DRAFT/CANCELLED
    disarida: bu ekran KARAR ekrani. Taslaga dondurmek ve iptal
    talebin sahibine ait islemler, karar verenin kuyrugunda
    durmalari kafa karistirirdi.
  */
  const secenekler = talep.izinliGecisler.filter(
    (d) => d !== "DRAFT" && d !== "CANCELLED" && d !== "PENDING",
  );

  const [hedef, setHedef] = useState<OnayDurumu>(secenekler[0] ?? "APPROVED");
  const [gerekce, setGerekce] = useState("");

  const karar = useMutation({
    mutationFn: () => genelOnayKarar(talep.id, hedef, gerekce.trim() || null),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["genel-onaylar"] });
      onBitti();
    },
  });

  const gerekceZorunlu = GEREKCE_ISTEYEN.includes(hedef);
  const gerekceEksik = gerekceZorunlu && gerekce.trim().length < 3;

  if (secenekler.length === 0) {
    return (
      <p className="text-notr-700 text-sm">
        Bu talep karara bağlanmış; yeniden açılamaz. Gerekiyorsa yeni bir talep oluşturulmalı.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-notr-700 text-sm">
        <p>
          <span className="font-medium">{talep.talepSahibi ?? "Bir kullanıcı"}</span> ·{" "}
          {TUR_ADI[talep.entityType] ?? talep.entityType}
          {talep.ogrenciAdi && ` · ${talep.ogrenciAdi}`}
        </p>
        {talep.summary && <p className="mt-1 whitespace-pre-wrap">{talep.summary}</p>}
      </div>

      {/*
        ================================================================
        KARAR AÇILIR LİSTE DEĞİL, DÜĞME — ÇİZİMDEN
        ================================================================
        `KuyrukEkranlari.dc.html`: "IZINLI GECISLER: dugmeler SATIR SATIR
        degisiyor". `Kuyruk.dc.html` de iki tonu ayrı ayrı yazıyor:
        "KABUL: birincil (gradyan-birincil)" ve "RET: dolu kirmizi
        DEGIL. Ret yikici degil, KESIN."

        Önceki hâlde karar bir `<select>`ti ve iki sonuç **aynı
        görünüyordu**: kabul ile ret arasındaki fark, açılır listede iki
        satırdı. Kararın ağırlığı düğmenin tonundan okunmuyordu ve karar
        iki tıklama sürüyordu (seç, sonra kaydet).

        Seçenekler yine SUNUCUDAN (`izinliGecisler`): düğme listesi
        satır satır değişiyor. Ekran kendi geçiş tablosunu tutmuyor —
        tutsaydı sunucu kuralı değiştiği gün olmayan bir karara düğme
        çizerdi.

        Ret dolu kırmızı DEĞİL (`gorunum="tehlike"` halkalı): dolu
        kırmızı "geri alınamaz felaket" tonu verir ve gerçek tehlike
        düğmelerini değersizleştirir.
      */}
      <div className="flex flex-wrap gap-2">
        {secenekler.map((d) => (
          <Buton
            key={d}
            gorunum={KARAR_GORUNUMU[d] ?? "ikincil"}
            aria-pressed={hedef === d}
            onClick={() => setHedef(d)}
            className={hedef === d ? "ring-marka-600 ring-2 ring-offset-2" : undefined}
          >
            {DURUM_ADI[d]}
          </Buton>
        ))}
      </div>

      <MetinAlani
        etiket={gerekceZorunlu ? "Gerekçe (zorunlu)" : "Gerekçe"}
        rows={3}
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
        yardim={
          gerekceZorunlu
            ? "Red ve revizyon isteğinde gerekçe zorunlu — talebi açan kişi ne yapacağını buradan öğrenecek."
            : "İsteğe bağlı. Talebi açan kişi görecek."
        }
      />

      {karar.isError && (
        <p role="alert" className="text-hata-500 text-sm font-medium">
          {karar.error instanceof ApiHatasi ? karar.error.message : "Karar kaydedilemedi."}
        </p>
      )}

      <div>
        <Buton
          yukleniyor={karar.isPending}
          /*
            Zorunlu gerekce yazilmadan dugme KAPALI. Sunucu zaten
            reddediyor; acik birakmak, yazdigi karari kaydettigini
            saniip ekrandan cikacak birine hata gostermek olurdu.
          */
          disabled={gerekceEksik}
          onClick={() => karar.mutate()}
        >
          {/*
            Dugme SECILEN karari yaziyor: iki asamali bir akista
            "Kararı kaydet" hangi karari kaydettigini soylemiyordu ve
            yanlis dugmeye basan kisi ancak sonuctan anliyordu.
          */}
          {DURUM_ADI[hedef]} olarak kaydet
        </Buton>
        <p className="text-notr-600 mt-1.5 text-xs">
          Karar bir kez verilir; karara bağlanmış talep yeniden açılmaz.
        </p>
      </div>
    </div>
  );
}
